import React from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useServiceWorkerUpdate } from '../hooks/useServiceWorkerUpdate';

/**
 * Persistent, non-dismissible snackbar that drives the PWA update flow.
 * Mounted once near the App root so it surfaces on every route, including
 * the LED / Broadcast full-screen views.
 */
const UpdatePrompt: React.FC = () => {
  const { t } = useTranslation();
  const { status, applyUpdate } = useServiceWorkerUpdate();

  const open = status !== 'idle';

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={false}
        sx={{ width: '100%', minWidth: 280, maxWidth: 480 }}
      >
        {status === 'installing' && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('update.downloading', 'Downloading update…')}
            </Typography>
            <LinearProgress color="inherit" />
          </Box>
        )}

        {status === 'ready' && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'center', width: '100%' }}
          >
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {t(
                'update.ready',
                'A new version of marX mobile is available.'
              )}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={applyUpdate}
            >
              {t('update.restartNow', 'Restart now')}
            </Button>
          </Stack>
        )}

        {status === 'activating' && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'center', width: '100%' }}
          >
            <CircularProgress size={20} color="inherit" />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {t(
                'update.activating',
                'Applying update, please wait…'
              )}
            </Typography>
          </Stack>
        )}
      </Alert>
    </Snackbar>
  );
};

export default UpdatePrompt;
